import { useEffect, useMemo } from "react";
import PropTypes from "prop-types";
import { toast, ActionCard, tokens, IconStar, IconInfo } from "@cobee/cobee-ui";
import qs from "qs";
import { useTranslation } from "react-i18next";
import { Grid, Typography } from "@material-ui/core";
import { useTreatments, withSplitFactory } from "@splitsoftware/splitio-react";
import sdkConfig from "infrastructure/split/sdkConfig";
import SubsidySection from "v2/ui/components/SubsidySection";
import MultipleSelect from "app/components/MultipleSelect";
import editBenefitPlan from "usecases/benefit-plan/edit-benefit-plan";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import CobeeButton from "app/components/CobeeButton";
import CobeeAlert from "app/components/CobeeAlert";
import BenefitRequests from "v2/ui/components/BenefitRequests";
import BenefitsTabs from "app/components/benefitsTabs/BenefitsTabs";
import WarningFlexAdvice from "app/components/benefitsTabs/warningFlexAdvice";
import useFindBenefits from "v2/ui/hooks/useFindBenefits";
import featureFlagsDictionary from "infrastructure/split/feature-flags-dictionary";
import { displayDeleteBenefitModal } from "../displays/display-delete-benefit-modal";
import { displayBenefitPage } from "../displays/display-benefit-page";
import { editBenefitModel, editBenefitModelV2 } from "../edit-benefit-model";
import { translate } from "../../../infrastructure/translate/localization";
import alternateCountryTranslations from "../../../infrastructure/translate/alternateCountyTranslations";
import { shouldShowLegalAdvice } from "../../../infrastructure/country-rules";

import "./benefit-page.scss";
import startBenefitPlanProcess from "../../benefit-plan/start-benefit-plan-process";
import analytics from "../../../app/segment";
import events from "../../../app/segment/events";

const BenefitPage = ({
  modelId,
  benefitModelIds,
  listItems,
  insuranceProducts,
  country,
  benefitConfigs,
  v2BenefitsConfig,
  companyId,
  companyGymBenefit,
}) => {
  const { t } = useTranslation();

  const treatments = useTreatments(
    [
      featureFlagsDictionary.BI_2406,
      featureFlagsDictionary.SMES_2112,
      featureFlagsDictionary.SMES_2223,
    ],
    {
      companyId,
      countryISOCode: country,
    }
  );
  const showRequestExperience =
    treatments[featureFlagsDictionary.BI_2406].treatment === "on";
  const employeeGroupsEnabled =
    treatments[featureFlagsDictionary.SMES_2112].treatment === "on";

  const selectedBenefitModel = useMemo(() => {
    if (modelId && listItems[modelId]) {
      return listItems[modelId];
    }
    return listItems[benefitModelIds[0]];
  }, [modelId, listItems, benefitModelIds]);

  const { data: benefitsV2 } = useFindBenefits({
    companyId,
    benefitModelId: selectedBenefitModel.id,
  });

  useEffect(() => {
    if (window.location.search) {
      const message = qs.parse(window.location.search.replace("?", ""))[
        "toast-message"
      ];
      toast.show(t(message), {
        type: message.includes("error") ? "error" : "success",
      });
      setTimeout(() => {
        if (window.history.pushState) {
          const newUrl = `${window.location.protocol}//${window.location.host}${window.location.pathname}`;
          window.history.pushState({ path: newUrl }, "", newUrl);
        }
      }, 2000);
    }
  }, [t]);

  useEffect(() => {
    analytics.track(events.benefits.name, {
      title: events.benefits.properties.title,
      url: events.benefits.properties.url,
    });
  }, []);

  const updateBenefit = (id, benefit, isV2 = false, state = false) => {
    // TODO: remove hotfix default config when the insurance product is activated
    if (
      benefit === "healthInsuranceBenefit" &&
      (!listItems[id].insuranceConfig || !listItems[id].insuranceConfig.length)
    ) {
      listItems[id].insuranceConfig = insuranceProducts.map((insurance) => ({
        modalityId: insurance.modalityId,
        allowedFamilyMembers: ["employee"],
        subvention: {
          employee: 0,
          partner: 0,
          descendant: 0,
        },
      }));
    }

    if (isV2) {
      editBenefitModelV2({
        benefitId: listItems[id].id,
        state,
        benefitType: benefit,
      });
    } else {
      editBenefitModel({
        ...listItems[id],
        [benefit]: !listItems[id][benefit],
      });
    }
  };

  const handlerToggleActive = (id, benefit, isV2, state) => {
    updateBenefit(id, benefit, isV2, state);
  };

  const handlerDisableAccumulation = (id, benefit, accumulationBenefit) => {
    if (listItems[id][benefit] && listItems[id][accumulationBenefit]) {
      editBenefitModel({
        ...listItems[id],
        [benefit]: !listItems[id][benefit],
        [accumulationBenefit]: !listItems[id][accumulationBenefit],
      });
    } else {
      handlerToggleActive(id, benefit);
    }
  };

  const showLegalBenefitNotice = () => {
    if (benefitModelIds.length < 2) {
      return false;
    }
    const { healthInsuranceBenefit, nurseryBenefit } = selectedBenefitModel;
    return (
      shouldShowLegalAdvice(country) &&
      (healthInsuranceBenefit || nurseryBenefit)
    );
  };

  return (
    <main className="section">
      <div className="section-header">
        <h1 className="section-title">
          {translate("benefits-section.benefits")}
        </h1>
        <div id="notification-card" />
      </div>

      <div className="benefit-request-container">
        <ActionCard
          title={t("v2.benefits.catalogue.name")}
          description={t("v2.benefits.catalogue.description")}
          icon={<IconStar color={tokens.palette.common.white} />}
          onClick={() => window.location.assign(`/v2/benefits/catalogue`)}
          showChevron
        />
      </div>
      {!showRequestExperience && (
        <div className="benefit-request-container">
          <BenefitRequests
            companyId={companyId}
            country={country}
            benefitsV2={benefitsV2}
          />
        </div>
      )}

      {employeeGroupsEnabled && (
        <div className="benefits-section">
          <CobeeAlert
            actionButtonsProps={[
              {
                handleClick: () =>
                  window.location.assign("/v2/employees/groups"),
                linkText: translate("v2.employee-groups.see-employee-groups"),
              },
            ]}
            title={translate("v2.employee-groups.name")}
            icon={<IconInfo color={tokens.palette.info.main} size={24} />}
          >
            {translate("v2.employee-groups.benefits-alert")}
          </CobeeAlert>
        </div>
      )}

      {benefitModelIds.length !== 0 ? (
        <>
          <Grid container alignItems="center" className="select-model-row">
            <div>
              <MultipleSelect
                placeholder={translate("benefits-section.current-models")}
                multiple={false}
                resetOption={false}
                onChange={(event) => displayBenefitPage(event.target.value)}
                initValues={selectedBenefitModel.id}
                list={Object.values(listItems).map(({ id, name }) => ({
                  value: id,
                  name,
                }))}
                selectActiveType="secondary"
              />
            </div>
            {!employeeGroupsEnabled && (
              <CobeeButton
                className="add-icon-style"
                onClick={startBenefitPlanProcess}
              >
                <FontAwesomeIcon icon="plus" alt="create" />
                <span style={{ margin: "0 15px 0" }}>
                  {translate("benefits-section.create-new-model")}
                </span>
              </CobeeButton>
            )}
          </Grid>

          <div className="benefits-section">
            <div className="card">
              <div className="card-body">
                <>
                  <Grid container alignItems="center">
                    <Typography
                      className="model-name"
                      title={selectedBenefitModel.name}
                    >
                      {selectedBenefitModel.name}
                    </Typography>
                    {!employeeGroupsEnabled && (
                      <div className="tab-actions">
                        <FontAwesomeIcon
                          icon="pen"
                          alt="edit"
                          data-tip={translate(
                            "benefits-section.edit-title-model"
                          )}
                          onClick={() =>
                            editBenefitPlan({
                              name: selectedBenefitModel.name,
                            })
                          }
                        />
                        {benefitModelIds.length > 1 && (
                          <FontAwesomeIcon
                            icon="trash"
                            alt="delete"
                            data-tip={translate(
                              "benefits-section.remove-model"
                            )}
                            onClick={() =>
                              displayDeleteBenefitModal(selectedBenefitModel)
                            }
                          />
                        )}
                      </div>
                    )}
                  </Grid>

                  <div className="divider" />
                </>

                {showLegalBenefitNotice() && (
                  <WarningFlexAdvice
                    text={alternateCountryTranslations(
                      country,
                      "legal-notice-text"
                    )}
                    isCustom
                  />
                )}
                <BenefitsTabs
                  insuranceProducts={insuranceProducts}
                  selectedTab={selectedBenefitModel}
                  disableAccumulation={(id, benefit, accumulationBenefit) =>
                    handlerDisableAccumulation(id, benefit, accumulationBenefit)
                  }
                  toggleActive={(id, benefit, isV2, state) =>
                    handlerToggleActive(id, benefit, isV2, state)
                  }
                  hasRenting={benefitConfigs?.["renting-benefit"]?.enabled}
                  hasLifeInsurance={
                    benefitConfigs?.["life-insurance-benefit"]?.enabled
                  }
                  benefitConfigs={benefitConfigs}
                  country={country}
                  v2BenefitsConfig={v2BenefitsConfig}
                  companyGymBenefit={companyGymBenefit}
                  benefitsV2={benefitsV2}
                />
              </div>
            </div>
          </div>

          <SubsidySection
            country={country}
            benefitModel={selectedBenefitModel}
          />
        </>
      ) : (
        <div className="benefits-section">
          <div className="card">
            <div className="card-body">
              <p>{translate("common-messages.loading-data")}</p>
            </div>
          </div>
        </div>
      )}
    </main>
  );
};

BenefitPage.defaultProps = {
  insuranceProducts: [],
  country: "ES",
  companyId: "",
  companyGymBenefit: undefined,
};

BenefitPage.propTypes = {
  modelId: PropTypes.string.isRequired,
  benefitModelIds: PropTypes.array.isRequired,
  listItems: PropTypes.object.isRequired,
  insuranceProducts: PropTypes.array,
  benefitConfigs: PropTypes.object.isRequired,
  v2BenefitsConfig: PropTypes.object.isRequired,
  country: PropTypes.string,
  companyId: PropTypes.string,
  companyGymBenefit: PropTypes.object,
};

export default withSplitFactory(sdkConfig)(BenefitPage);
